import React, { useState } from 'react';
import { useStore } from '../hooks/useStore';
import { useToast } from '../App';
import { PlusIcon, MinusIcon, ShoppingCartIcon } from 'lucide-react';
import { DoorStrip, DOOR_PALETTE, WOOD_TEXTURE_CSS } from '../components/DoorStrip';
const ITEM_IMAGES: Record<string, string> = {
  '1': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&q=80',
  '2': 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&q=80',
  '3': 'https://images.unsplash.com/photo-1541014741259-de529411b96a?w=400&q=80',
  '4': 'https://images.unsplash.com/photo-1621263764928-df1444c5e859?w=400&q=80',
  '5': 'https://images.unsplash.com/photo-1606313564200-e75d5e30476c?w=400&q=80',
  '6': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=400&q=80',
  '7': 'https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?w=400&q=80',
  '8': 'https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=400&q=80'
};
const CATEGORY_COLORS: Record<string, string> = {
  Entradas: DOOR_PALETTE[0],
  'Platos Fuertes': DOOR_PALETTE[1],
  Ensaladas: DOOR_PALETTE[3],
  Bebidas: DOOR_PALETTE[2],
  Postres: DOOR_PALETTE[4]
};
export function OrderPage({
  onOpenCart
}: {
  onOpenCart?: () => void;
}) {
  const {
    menuItems,
    addToCart,
    cart,
    updateCartQuantity
  } = useStore();
  const {
    showToast
  } = useToast();
  const categories = Array.from(new Set(menuItems.map(item => item.category)));
  const [activeCategory, setActiveCategory] = useState<string>(categories[0] || '');
  const getCartQty = (itemId: string) => {
    const found = cart.find(c => c.menuItem.id === itemId);
    return found ? found.quantity : 0;
  };
  const handleAdd = (item: (typeof menuItems)[0]) => {
    addToCart(item);
    showToast(`${item.name} añadido al carrito`);
  };
  const activeColor = CATEGORY_COLORS[activeCategory] || '#333333';
  return <div className="min-h-screen bg-white" data-id="element-611">
      {/* ── HERO HEADER ── */}
      <div className="relative bg-black overflow-hidden pt-24 pb-12 px-6 text-center" data-id="element-612">
        <div className="absolute inset-0 opacity-10" style={{
        background: `radial-gradient(ellipse at center, ${activeColor} 0%, transparent 70%)`
      }} data-id="element-613" />
        <div className="relative z-10" data-id="element-614">
          <img src="https://cdn.magicpatterns.com/uploads/ewfDCj5QKNDNeQRsTcwjBG/logo_de_Junquito_Gas_(4).ai_(4).png" alt="La Cochera" className="h-12 mx-auto mb-6" data-id="element-615" />
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6" data-id="element-616">
            Haz tu Pedido
          </h1>
          <div className="flex justify-center gap-1 mb-0" data-id="element-617">
            {DOOR_PALETTE.map((c, i) => <div key={i} className="w-8 h-1 rounded-full" style={{
            backgroundColor: c
          }} data-id="element-618" />)}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" data-id="element-619">
          <DoorStrip height="h-2" data-id="element-620" />
        </div>
      </div>

      {/* ── CATEGORY NAV ── */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-md border-b border-stone-100 py-3" data-id="element-621">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-4" data-id="element-622">
          <div className="flex overflow-x-auto no-scrollbar gap-2 flex-1" data-id="element-623">
            {categories.map(cat => {
            const isActive = activeCategory === cat;
            const color = CATEGORY_COLORS[cat] || '#333333';
            return <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${isActive ? 'text-white shadow-md scale-105' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`} style={isActive ? {
              backgroundColor: color
            } : {}} data-id="element-624">
                  {cat}
                </button>;
          })}
          </div>

          {cart.length > 0 && onOpenCart && <button onClick={onOpenCart} className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-full font-bold text-xs hover:bg-stone-800 transition-colors shadow-lg flex-shrink-0" data-id="element-625">
              <ShoppingCartIcon className="w-3.5 h-3.5" data-id="element-626" />
              Carrito ({cart.reduce((s, c) => s + c.quantity, 0)})
            </button>}
        </div>
      </div>

      {/* ── SECTION BANNER ── */}
      <div className="relative overflow-hidden" style={{
      backgroundColor: activeColor,
      backgroundImage: WOOD_TEXTURE_CSS
    }} data-id="element-627">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between" data-id="element-628">
          <h2 className="font-display text-3xl font-extrabold text-white tracking-tight" data-id="element-629">
            {activeCategory}
          </h2>
          <span className="font-display text-6xl font-extrabold text-white/10 leading-none select-none" data-id="element-630">
            {menuItems.filter(item => item.category === activeCategory && item.available).length.toString().padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* ── ITEMS GRID ── */}
      <div className="max-w-7xl mx-auto px-6 py-12" data-id="element-631">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-632">
          {menuItems.filter(item => item.category === activeCategory && item.available).map(item => {
          const qty = getCartQty(item.id);
          return <div key={item.id} className="group relative bg-white rounded-xl overflow-hidden border border-stone-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col" data-id="element-633">
                  {/* Left accent bar */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 z-10" style={{
              backgroundColor: activeColor
            }} data-id="element-634" />

                  <div className="h-48 overflow-hidden relative" data-id="element-635">
                    <img src={ITEM_IMAGES[item.id] || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80'} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-636" />
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-lg text-xs font-extrabold text-black shadow-sm" data-id="element-637">
                      ${item.price.toFixed(2)}
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col pl-6" data-id="element-638">
                    <h3 className="font-display font-bold text-stone-900 text-lg mb-2 leading-tight" data-id="element-639">
                      {item.name}
                    </h3>
                    <p className="text-stone-500 text-sm leading-relaxed mb-6 flex-1 line-clamp-2 font-medium" data-id="element-640">
                      {item.description}
                    </p>

                    {qty === 0 ? <button onClick={() => handleAdd(item)} className="w-full py-3 bg-black text-white rounded-xl font-bold text-xs hover:bg-stone-800 transition-colors flex items-center justify-center gap-2 active:scale-[0.98]" data-id="element-641">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-642" /> Añadir
                      </button> : <div className="flex items-center justify-between bg-stone-50 rounded-xl p-1 border border-stone-100" data-id="element-643">
                        <button onClick={() => updateCartQuantity(item.id, qty - 1)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm transition-all text-black" data-id="element-644">
                          <MinusIcon className="w-3.5 h-3.5" data-id="element-645" />
                        </button>
                        <span className="font-bold text-black text-sm tabular-nums" data-id="element-646">
                          {qty}
                        </span>
                        <button onClick={() => {
                  addToCart(item);
                  showToast(`${item.name} añadido`);
                }} className="w-9 h-9 flex items-center justify-center rounded-lg bg-black text-white hover:bg-stone-800 transition-colors" data-id="element-647">
                          <PlusIcon className="w-3.5 h-3.5" data-id="element-648" />
                        </button>
                      </div>}
                  </div>
                </div>;
        })}
        </div>
      </div>
    </div>;
}